import { Download } from 'lucide-react';
import { Button, Mono, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';

/**
 * IFS model v2 (CREATE_IFS v4, ADR-032): artifacts (front-end/back-end), API products, interface
 * rows, and per operation the data models (seven GBA columns), mappings and examples. Models and
 * mappings export as CSV in the GBA layout for manual entry.
 */
interface PlanValue {
  value: string | null;
  status: string;
}
interface Artifacts {
  products: { key: string; reuse: string; name: PlanValue; shortDescription: string }[];
  artifacts: {
    key: string;
    side: 'front' | 'back';
    technology: string;
    action: string;
    existingRef: string | null;
    properties: Record<string, PlanValue>;
    products: string[];
    operations: {
      key: string;
      verb: string;
      path: string;
      description: string;
      backend: string | null;
    }[];
  }[];
  interfaces: {
    artifact: string;
    action: string;
    technology: string;
    name: string | null;
    version: string | null;
    operationText: string;
    backendOperation: string | null;
  }[];
}
interface OperationModels {
  operations: {
    operation: string;
    backend: string | null;
    models: {
      key: string;
      side: string;
      kind: string;
      httpStatus: string | null;
      elements: {
        elementName: PlanValue;
        location: string;
        gdpr: boolean;
        cardinality: string;
        type: string;
        details: string;
        description: string;
      }[];
    }[];
    mappings: {
      direction: string;
      httpStatus: string | null;
      source: string;
      transformation: string;
      target: string;
    }[];
    examples: { model: string; payload: string }[];
  }[];
}

const csv = (rows: string[][]) =>
  `${rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\r\n')}\r\n`;
const save = (name: string, text: string) => {
  const blob = new Blob(['\uFEFF', text], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

export function IfsArtifactsView({
  artifacts,
  models,
  fileBase,
}: {
  artifacts: Artifacts;
  models?: OperationModels;
  fileBase: string;
}) {
  const productName = (k: string) => artifacts.products.find((p) => p.key === k)?.name.value ?? k;
  const exportModels = () =>
    save(
      `${fileBase}-data-models.csv`,
      csv([
        [
          'Operation',
          'Model',
          'ElementName',
          'Location',
          'GDPR',
          'Cardinality',
          'Type',
          'Details',
          'Description',
        ],
        ...(models?.operations ?? []).flatMap((o) =>
          o.models.flatMap((m) =>
            m.elements.map((e) => [
              o.operation,
              m.key,
              e.elementName.value ?? '',
              e.location,
              e.gdpr ? 'x' : '',
              e.cardinality,
              e.type,
              e.details,
              e.description,
            ]),
          ),
        ),
      ]),
    );
  const exportMappings = () =>
    save(
      `${fileBase}-mappings.csv`,
      csv([
        [
          'Operation',
          'Structure',
          'HTTP code',
          'Source field',
          'Transformation Mapping',
          'Destination Fields',
        ],
        ...(models?.operations ?? []).flatMap((o) =>
          o.mappings.map((m) => [
            o.operation,
            m.direction === 'request' ? 'Request Structure' : 'Response Structure',
            m.httpStatus ?? '',
            m.source,
            m.transformation,
            m.target,
          ]),
        ),
      ]),
    );

  return (
    <section aria-labelledby="ifs-artifacts" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="ifs-artifacts" className="text-sm font-semibold">
          Artifacts, API products and interfaces
        </h3>
        {models && (
          <span className="ml-auto flex gap-2">
            <Button onClick={exportModels}>
              <Download aria-hidden="true" className="size-4" /> Data models (CSV)
            </Button>
            <Button onClick={exportMappings}>
              <Download aria-hidden="true" className="size-4" /> Mappings (CSV)
            </Button>
          </span>
        )}
      </div>

      {artifacts.products.length > 0 && (
        <ul className="space-y-1 text-sm">
          {artifacts.products.map((p) => (
            <li key={p.key}>
              API product <Mono>{p.name.value ?? '?'}</Mono> ({p.reuse}){' '}
              <DomainStatus value={p.name.status.toLowerCase()} />
              {p.reuse !== 'existing' && (
                <span className="block text-xs text-muted">
                  Name to confirm with the API centre of excellence. {p.shortDescription}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {artifacts.artifacts.map((a) => (
        <div key={a.key} className="rounded-md border border-border p-3">
          <p className="text-sm font-medium">
            {a.side === 'front'
              ? 'Integration artifact (front-end)'
              : 'Non-integration artifact (back-end)'}{' '}
            · {a.properties.name?.value ?? a.key} {a.properties.version?.value ?? ''} ·{' '}
            {a.technology} · {a.action}
            {a.existingRef && (
              <>
                {' '}
                · existing <Mono>{a.existingRef}</Mono>
              </>
            )}
          </p>
          {a.products.length > 0 && (
            <p className="text-xs text-muted">Products: {a.products.map(productName).join(', ')}</p>
          )}
          <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {Object.entries(a.properties).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="w-36 shrink-0 text-muted">{k}</dt>
                <dd>
                  {v.value ?? '—'} <span className="text-muted">({v.status.toLowerCase()})</span>
                </dd>
              </div>
            ))}
          </dl>
          <Table className="mt-2">
            <thead>
              <tr>
                <Th>Operation</Th>
                <Th>Description</Th>
                {a.side === 'front' && <Th>Calls (back-end)</Th>}
              </tr>
            </thead>
            <tbody>
              {a.operations.map((o) => (
                <tr key={o.key}>
                  <Td>
                    <Mono>{`${o.verb} ${o.path}`}</Mono>
                  </Td>
                  <Td>{o.description}</Td>
                  {a.side === 'front' && (
                    <Td>
                      {artifacts.interfaces.find((i) => i.operationText === `${o.verb} ${o.path}`)
                        ?.backendOperation ?? <span className="text-fail">not linked</span>}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ))}

      {models?.operations.map((o) => (
        <details key={o.operation} className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Data models and mappings · <Mono>{o.operation}</Mono>
            {o.backend && <span className="text-xs text-muted"> → {o.backend}</span>}
          </summary>
          {o.models.map((m) => (
            <div key={m.key} className="mt-3 overflow-x-auto">
              <p className="text-xs font-medium">
                {m.side === 'front' ? 'Front-end' : 'Back-end'} {m.kind}
                {m.httpStatus ? ` ${m.httpStatus}` : ''}
              </p>
              <Table>
                <thead>
                  <tr>
                    {[
                      'ElementName',
                      'Location',
                      'GDPR',
                      'Cardinality',
                      'Type',
                      'Details',
                      'Description',
                    ].map((h) => (
                      <Th key={h}>{h}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {m.elements.map((e, i) => (
                    <tr key={i}>
                      <Td>
                        <Mono>{e.elementName.value ?? '?'}</Mono>
                      </Td>
                      <Td>{e.location}</Td>
                      <Td>{e.gdpr ? 'x' : ''}</Td>
                      <Td>{e.cardinality}</Td>
                      <Td>{e.type}</Td>
                      <Td>{e.details}</Td>
                      <Td>{e.description}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
          {o.mappings.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <p className="text-xs font-medium">Mappings</p>
              <Table>
                <thead>
                  <tr>
                    <Th>Structure</Th>
                    <Th>HTTP</Th>
                    <Th>Source</Th>
                    <Th>Transformation</Th>
                    <Th>Destination</Th>
                  </tr>
                </thead>
                <tbody>
                  {o.mappings.map((m, i) => (
                    <tr key={i}>
                      <Td>{m.direction}</Td>
                      <Td>{m.httpStatus ?? ''}</Td>
                      <Td>
                        <Mono>{m.source}</Mono>
                      </Td>
                      <Td>{m.transformation}</Td>
                      <Td>
                        <Mono>{m.target}</Mono>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {o.examples.map((x, i) => (
            <div key={i} className="mt-3">
              <p className="text-xs font-medium">
                Example · {x.model} (illustrative, no real data)
              </p>
              <pre className="overflow-x-auto rounded-md bg-surface-muted p-2 text-xs">
                {x.payload}
              </pre>
            </div>
          ))}
        </details>
      ))}
    </section>
  );
}
